/**
 * Rendu d'un tableau du contrat (§1 « Tableaux ») à partir de ses colonnes
 * typées : la même définition sert au JSON et à l'export CSV, le front ne fait
 * que choisir le formateur du type.
 *
 * Tri, recherche et pagination sont faits par le serveur. Deux modes :
 * - `server` fourni : l'écran relaie tri et page au serveur (nouvelle requête) ;
 * - sinon : les lignes reçues sont affichées telles quelles, et si le serveur
 *   en a plus (`total_rows`), un avertissement le dit — jamais de pagination
 *   ni de filtre côté client sur un extrait.
 */
import React from 'react';
import { Space, Table as AntTable, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import {
    COST_STATUSES,
    type Cost,
    type CostStatusCounts,
    type Learner,
    type Money,
    type MoneyMulti,
    type Ratio,
    type SortOrder,
    type Table,
    type TableColumn,
    type Tokens,
} from '../../../api/monitoring/v2';
import { CostText, MoneyMultiText, MoneyText } from './MoneyText';
import CompactValue from './CompactValue';
import RatioText from './RatioText';
import { useEnumLabel } from './i18nCatalog';
import { useFormat } from './format';
import { INK } from '../monitoringTheme';

const { Text } = Typography;

export type ServerTableState = {
    sort?: string;
    order?: SortOrder;
    page: number;
    pageSize: number;
};

export type DataTableProps<Row> = {
    table: Table<Row> | null;
    rowKey: (row: Row, index?: number) => string;
    loading?: boolean;
    server?: {
        state: ServerTableState;
        onChange: (next: ServerTableState) => void;
    };
    /** Colonnes ajoutées à droite (actions). */
    extraColumns?: ColumnsType<Row>;
    emptyText?: string;
    /** Masquer des colonnes (ex. coûts pour un non-admin, s'ils étaient présents). */
    hiddenKeys?: readonly string[];
    /** Rendu propre à l'écran pour certaines colonnes (ex. statut coloré). */
    cellOverrides?: Record<string, (value: unknown, row: Row) => React.ReactNode>;
};

const warned = new Set<string>();
const warnOnce = (msg: string, detail: unknown) => {
    if (warned.has(msg)) return;
    warned.add(msg);
    // eslint-disable-next-line no-console
    console.warn(msg, detail);
};

/** Renderer d'une cellule selon le type déclaré par le serveur. */
export const useCellRenderer = () => {
    const f = useFormat();
    const enumLabel = useEnumLabel();
    const { t } = useTranslation();

    return (column: TableColumn, value: unknown): React.ReactNode => {
        if (value === null || value === undefined) return <Text style={{ color: INK.muted }}>—</Text>;
        const type = column.type;
        if (type.startsWith('enum:')) {
            return <Tag bordered={false}>{enumLabel(type.slice(5), String(value))}</Tag>;
        }
        switch (type) {
            case 'text':
                return String(value);
            case 'count':
                // Un identifiant se lit en entier ; les comptages, en compact.
                return column.key === 'id' ? f.count(value as number) : <CompactValue value={value as number} />;
            case 'money': {
                const m = value as Money;
                return <MoneyText amount={m.amount} currency={m.currency} />;
            }
            case 'money_multi':
                return <MoneyMultiText values={value as MoneyMulti} />;
            case 'cost':
                return <CostText cost={value as Cost} />;
            case 'ratio':
                return <RatioText ratio={value as Ratio} />;
            case 'date':
                return f.date(value as string);
            case 'datetime':
                // Heure murale fournie par le serveur, affichée telle quelle (v2.2 §11).
                return f.dateTime(value as string);
            case 'tokens': {
                // Forme IA-04 (§10.6) : entrée → sortie, détail en dessous ;
                // un compteur inconnu reste « — », jamais 0.
                const tk = value as Tokens;
                const detail = t('monitoring.tokens.detail', { cached: f.compact(tk.cached).text, reasoning: f.compact(tk.reasoning).text });
                const detailExact = t('monitoring.tokens.detail', { cached: f.count(tk.cached), reasoning: f.count(tk.reasoning) });
                return (
                    <Space direction="vertical" size={0} style={{ textAlign: 'end' }}>
                        <Text style={{ fontSize: 12 }} dir="ltr">
                            <CompactValue value={tk.input} /> → <CompactValue value={tk.output} />
                        </Text>
                        <Tooltip title={detailExact === detail ? undefined : detailExact}>
                            <Text style={{ fontSize: 11, color: INK.muted }}>{detail}</Text>
                        </Tooltip>
                    </Space>
                );
            }
            case 'cost_status_counts': {
                const counts = value as CostStatusCounts;
                return (
                    <Space size={4} wrap>
                        {COST_STATUSES.map((code) => (
                            <Tag key={code} bordered={false} data-cost-status={code}>
                                {enumLabel('cost_status', code)} : <CompactValue value={counts[code]} />
                            </Tag>
                        ))}
                    </Space>
                );
            }
            case 'learner': {
                const learner = value as Learner;
                if (!learner) return <Text style={{ color: INK.muted }}>{t('monitoring.traces.noLearner')}</Text>;
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 13 }}>
                            {learner.name ?? t('monitoring.traces.learnerHidden')}
                        </Text>
                        <Text style={{ fontSize: 11, color: INK.muted }}>#{learner.id}</Text>
                    </Space>
                );
            }
            default: {
                // Type hors contrat : montré brut et signalé, jamais caché.
                warnOnce(`[monitoring] type de colonne hors contrat : ${type}`, column.key);
                return typeof value === 'object' ? JSON.stringify(value) : String(value);
            }
        }
    };
};

const ALIGN_END = new Set(['count', 'money', 'money_multi', 'cost', 'ratio', 'tokens']);

function DataTable<Row extends Record<string, unknown>>({
    table,
    rowKey,
    loading,
    server,
    extraColumns,
    emptyText,
    hiddenKeys,
    cellOverrides,
}: DataTableProps<Row>) {
    const { t, i18n } = useTranslation();
    const f = useFormat();
    const renderCell = useCellRenderer();

    const columns: ColumnsType<Row> = [
        ...(table?.columns ?? [])
            .filter((col) => !hiddenKeys?.includes(col.key))
            .map((col) => {
                if (!i18n.exists(col.label)) warnOnce(`[monitoring] libellé de colonne manquant : ${col.label}`, col.key);
                const sortOrder: 'ascend' | 'descend' | null =
                    server?.state.sort === col.key ? (server.state.order === 'asc' ? 'ascend' : 'descend') : null;
                return {
                    key: col.key,
                    dataIndex: col.key,
                    title: t(col.label),
                    align: ALIGN_END.has(col.type) ? ('end' as const) : undefined,
                    sorter: server ? true : undefined,
                    sortOrder: server ? sortOrder : undefined,
                    render: (_: unknown, row: Row) =>
                        cellOverrides?.[col.key] ? cellOverrides[col.key](row[col.key], row) : renderCell(col, row[col.key]),
                };
            }),
        ...(extraColumns ?? []),
    ];

    const pagination: TablePaginationConfig | false = server
        ? {
              current: server.state.page,
              pageSize: server.state.pageSize,
              total: table?.total_rows ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              showTotal: (total) => t('monitoring.table.total', { n: f.count(total) }),
          }
        : false;

    const handleChange = (
        p: TablePaginationConfig,
        _filters: unknown,
        sorter: SorterResult<Row> | SorterResult<Row>[]
    ) => {
        if (!server) return;
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const sortKey = s?.order ? String(s.columnKey ?? s.field) : undefined;
        const order: SortOrder | undefined = s?.order ? (s.order === 'ascend' ? 'asc' : 'desc') : undefined;
        const sortChanged = sortKey !== server.state.sort || order !== server.state.order;
        const pageSize = p.pageSize ?? server.state.pageSize;
        server.onChange({
            sort: sortKey,
            order,
            pageSize,
            // Un changement de tri ou de taille repart de la première page.
            page: sortChanged || pageSize !== server.state.pageSize ? 1 : p.current ?? 1,
        });
    };

    const partial = !server && table && table.total_rows > table.rows.length;

    return (
        <>
            <AntTable<Row>
                size="small"
                rowKey={(row) => (row as Row & { __rowKey: string }).__rowKey}
                loading={loading}
                columns={columns}
                dataSource={(table?.rows ?? []).map((row, index) => ({ ...row, __rowKey: rowKey(row, index) }))}
                pagination={pagination}
                onChange={handleChange}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: emptyText ?? t('monitoring.table.empty') }}
            />
            {partial && (
                <Text style={{ display: 'block', marginTop: 8, fontSize: 12, color: INK.muted }} data-testid="table-partial">
                    {t('monitoring.table.partial', { shown: f.count(table!.rows.length), total: f.count(table!.total_rows) })}
                </Text>
            )}
        </>
    );
}

export default DataTable;
